/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { PageTitleBar } from 'carbon-addons-iot-react';
import * as Scroll from 'react-scroll';

const sectionPropTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  headerControls: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  description: PropTypes.string,
  content: PropTypes.node.isRequired,
};
const sectionDefaultProps = {
  headerControls: undefined,
  description: undefined,
  title: undefined,
};

const propTypes = {
  title: PropTypes.string,
  breadcrumb: PropTypes.arrayOf(PropTypes.node),
  sections: PropTypes.arrayOf(PropTypes.shape(sectionPropTypes)),
  extraContent: PropTypes.node,
  topSectionContent: PropTypes.node,
};

const defaultProps = {
  title: undefined,
  breadcrumb: [],
  extraContent: [],
  topSectionContent: [],
  sections: [],
};

export const DetailsPageMain = ({ children }) => (
  <div className="details-page--main">{children}</div>
);
DetailsPageMain.propTypes = {
  children: PropTypes.node.isRequired,
};

export const DetailsPageSection = ({ id, title, headerControls, description, content }) => (
  <Scroll.Element key={`${id}-element`} name={id} className="details-page--main--section">
    <div className="details-page--main--section--header">
      <h4 className="details-page--main--section--header--title">{title}</h4>
      <div className="details-page--main--section--header--controls">{headerControls}</div>
    </div>
    <div className="details-page--main--section--description">{description}</div>
    <div className="details-page--main--section--content">{content}</div>
  </Scroll.Element>
);
DetailsPageSection.propTypes = sectionPropTypes;
DetailsPageSection.defaultProps = sectionDefaultProps;

const DetailsPage = ({ title, breadcrumb, sections, extraContent, topSectionContent }) => {
  // used to determine vertical offset (due to e.g. the main header)
  // when scrolling to elements in the page
  const pageRef = useRef(null);

  return (
    <PageTitleBar
      title={title}
      breadcrumb={breadcrumb}
      extraContent={extraContent}
      content={
        <div className="details-page" ref={pageRef}>
          <div />
          <DetailsPageMain>
            {topSectionContent}
            {sections.map(section => (
              <DetailsPageSection
                id={section.id}
                title={section.title}
                headerControls={section.headerControls}
                description={section.description}
                content={section.content}
                key={section.id}
              />
            ))}
          </DetailsPageMain>
        </div>
      }
    />
  );
};

DetailsPage.propTypes = propTypes;
DetailsPage.defaultProps = defaultProps;

export default DetailsPage;
